import React from 'react';
import { Shop } from '../services/shopService';
import { getValidImageUrl } from '../utils/imageUtils';
import './ShopDetails.css';

interface ShopDetailsProps {
  shop: Shop;
  onClose: () => void;
  onSelectShop: (shop: Shop) => void;
}

const ShopDetails: React.FC<ShopDetailsProps> = ({ shop, onClose, onSelectShop }) => {
  const getShopTypeIcon = (type: string) => {
    const icons: { [key: string]: string } = {
      grocery: '🛒',
      pharmacy: '💊',
      bakery: '🥖',
      electronics: '📱',
      clothing: '👕',
      medical: '🏥',
      restaurant: '🍽️',
      general: '🏪'
    };
    return icons[type] || '🏪';
  };

  const formatAddress = () => {
    return `${shop.address}, ${shop.city}, ${shop.state} ${shop.country}`;
  };

  return (
    <div className="shop-details-overlay">
      <div className="shop-details-modal">
        <div className="shop-details-header">
          <div className="shop-details-title">
            <div className="shop-logo-container">
              {shop.logo || shop.logo_url || shop.image || shop.image_url || shop.photo ? (
                <img 
                  src={getValidImageUrl(shop.logo || shop.logo_url || shop.image || shop.image_url || shop.photo || '')}
                  alt={shop.name}
                  className="shop-logo-image"
                  onError={(e) => {
                    console.error('Failed to load shop logo in ShopDetails:', shop.logo || shop.logo_url, 'for shop:', shop.name);
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent && !parent.querySelector('.shop-icon-large')) {
                      const fallback = document.createElement('div');
                      fallback.className = 'shop-icon-large';
                      fallback.textContent = getShopTypeIcon(shop.type);
                      parent.appendChild(fallback);
                    }
                  }}
                  onLoad={() => {
                    console.log('Successfully loaded shop logo in ShopDetails:', shop.logo || shop.logo_url, 'for shop:', shop.name);
                  }}
                />
              ) : (
                <div className="shop-icon-large">{getShopTypeIcon(shop.type)}</div>
              )}
            </div>
            <div>
              <h2>{shop.name}</h2>
              <p className="shop-type-badge">{shop.type}</p>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="shop-details-content">
          <div className="shop-info-section">
            <h3>📍 Location</h3>
            <p className="shop-address">{formatAddress()}</p>
          </div>

          {shop.description && (
            <div className="shop-info-section">
              <h3>📝 Description</h3>
              <p>{shop.description}</p>
            </div>
          )}

          <div className="shop-contact-section">
            <h3>📞 Contact Information</h3>
            <div className="contact-grid">
              {shop.phone && (
                <div className="contact-item">
                  <span className="contact-label">Phone:</span>
                  <span className="contact-value">{shop.phone}</span>
                </div>
              )}
              {shop.email && (
                <div className="contact-item">
                  <span className="contact-label">Email:</span>
                  <span className="contact-value">{shop.email}</span>
                </div>
              )}
            </div>
          </div>

          <div className="shop-status-section">
            <h3>🏪 Shop Status</h3>
            <div className="status-info">
              <div className={`status-badge ${shop.is_active ? 'active' : 'inactive'}`}>
                {shop.is_active ? '🟢 Open' : '🔴 Closed'}
              </div>
              <p className="status-text">
                {shop.is_active 
                  ? 'This shop is currently open and accepting orders' 
                  : 'This shop is currently closed or unavailable'
                }
              </p>
            </div>
          </div>

          {shop.theme && (
            <div className="shop-theme-section">
              <h3>🎨 Shop Theme</h3>
              <div className="theme-preview">
                <div 
                  className="theme-color" 
                  style={{ backgroundColor: shop.theme.primary_color }}
                  title="Primary Color"
                ></div>
                <div 
                  className="theme-color" 
                  style={{ backgroundColor: shop.theme.secondary_color }}
                  title="Secondary Color"
                ></div>
                <div 
                  className="theme-color" 
                  style={{ backgroundColor: shop.theme.accent_color }}
                  title="Accent Color"
                ></div>
              </div>
            </div>
          )}
        </div>

        <div className="shop-details-actions">
          <button className="secondary-btn" onClick={onClose}>
            Close
          </button>
          <button 
            className="primary-btn" 
            onClick={() => {
              onSelectShop(shop);
              onClose();
            }}
            disabled={!shop.is_active}
          >
            {shop.is_active ? 'Browse Products' : 'Shop Unavailable'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShopDetails;
